/* eslint-disable prettier/prettier */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { Component, namespace, Vue } from 'nuxt-property-decorator';
import { MetaPropertyName } from 'vue-meta';

import { IWishlistItem } from '../entities/wishlist-item-entity';

import { namespace as wishlistStore, actionType as wishlistActionType } from '../store/modules/wishlist-store';
import { namespace as cartStore, actionType as cartActionType, IAddItemToCartBody } from '../store/modules/cart-store';
import {
	namespace as productsStore, actionType,
	IGetProductParams, IRelatedProductParams
} from '../store/modules/product-store';
import { IProduct } from '../entities/product-entity';
import { IRelatedProduct } from '../entities/related-product-entity';
import { IProductModification } from '../entities/product-modification-entity';
import { IProductVariant } from '../entities/product-variant-entity';

const CartStore = namespace(cartStore);
const ProductsStore = namespace(productsStore);
const WishlistStore = namespace(wishlistStore);

const getObjectValuesAsNumber = (obj: Record<string, any>): number[] => Object.values(obj).map(Number);

type ModifierSetsMap = Record<number, IModifierSet>;

type SelectedVariant = Record<number, number>;
type SelectedModifications = Record<number, number>;

export interface IModifierVariant {
	id: number;
	label: string;
	image: string | null | undefined;
}

export interface IModifierVariantOption extends IModifierVariant {
	enabled: boolean;
	selected: boolean;
}

export interface IModifierVariantOptionsGroup {
	id: number;
	name: string;
	variants: IModifierVariantOption[];
	selected: boolean;
	enabled: boolean;
}

export interface IModifierSet {
	modifier: IModifier | null;
	quantity: number | null;
	id: number | null;
	quantityLimit: number;
	price: number | null;
	oldPrice: number | null;
	productId: number;
	modifications: SelectedModifications;
}

export interface IModifier {
	id: number | null;
	productId: number;
	name: string | null;
	oldPrice?: number | null;
	price: number | null;
	variants: { id: number }[];
	quantity: number | null;
}

export interface IModifierQuantity {
	current: number;
	limit: number;
}

interface IPageHead {
	title: string;
	meta: MetaPropertyName[];
}

@Component({
	head(this: ProductMixin) {
		return this.getPageHead();
	},
	async fetch(this: ProductMixin) {
		await this.loadProduct();
	},
})
export default class ProductMixin extends Vue {
	@ProductsStore.Action(actionType.GET_PRODUCT_BY_SLUG)
	readonly getProduct!: (params: IGetProductParams) => Promise<IProduct>;

	@ProductsStore.Action(actionType.GET_RELATED_PRODUCTS)
	readonly actionGetRelatedProducts!: (params: IRelatedProductParams) => Promise<IRelatedProduct[]>;

	@ProductsStore.Action(actionType.GET_SAME_PRODUCTS)
	readonly actionGetSameProducts!: (params: IRelatedProductParams) => Promise<IRelatedProduct[]>;

	@ProductsStore.Action(actionType.GET_RECENTLY_PRODUCTS)
	readonly actionGetRecentlyProducts!: (params: IRelatedProductParams) => Promise<IRelatedProduct[]>;

	@ProductsStore.Getter
	readonly currentProduct!: IProduct;

	@ProductsStore.Getter
	readonly relatedProducts!: IRelatedProduct[];

	@ProductsStore.Getter
	readonly sameProducts!: IRelatedProduct[];

	@ProductsStore.Getter
	readonly recentlyProducts!: IRelatedProduct[];

	@ProductsStore.Getter
	readonly breadcrumbs!: any;

	@CartStore.Action(cartActionType.PUT_TO_CART)
	readonly putToCart!: (item: IAddItemToCartBody) => Promise<boolean>;

	@CartStore.Action(cartActionType.GET_CART)
	readonly getCart!: () => void;

	@WishlistStore.Action(wishlistActionType.ADD_TO_WISHLIST)
	readonly addItemToWishlist!: (id: number) => Promise<IWishlistItem>;

	@WishlistStore.Action(wishlistActionType.REMOVE_ITEM_FROM_WISHLIST)
	readonly removeItemFromWishlist!: (id: number) => Promise<IWishlistItem>;

	@WishlistStore.Getter
	readonly isInWishlist!: (id: number) => boolean;

	public modifierSets: IModifierSet[] = [];

	public product: IProduct | null = null;
	public $toast: any;

	get extensions(): string[] {
		return [];
	}
	get extensionsIRelatedProduct(): string[] {
		return [];
	}

	/**
	 * NOTE: место для подумать - если вернется пустой slug
	 * */
	public get slug(): string {
		if (this.currentProduct) {
			return this.currentProduct.slug || '';
		}
		return '';
	}

	public get modifierSetsCount(): number {
		return this.modifierSets.length;
	}

	public get hasModifierSets(): boolean {
		return this.modifierSetsCount > 0;
	}

	public get isSingleModifierSet(): boolean {
		return this.modifierSetsCount === 1;
	}

	/** Дефолтный модификатор */
	public get defaultModifierSet(): IModifierSet | undefined {

		return this.modifierSets[0] || undefined;
	}

	/** Товар может быть модифицирован */
	public get hasModifications(): boolean {
		return Boolean(this.product && this.product.hasModifications);
	}

	/** Проверяю, что все модификаторы установлены */
	public get modificationSelected(): boolean {
		if (this.hasModifications) {
			const availableModifications = this.product?.modifications.map(({ id }) => +id) || [];

			return this.modifierSets.every((modifierSet) => {
				if (modifierSet.modifier && modifierSet.modifier.id) {
					return availableModifications?.includes(modifierSet.modifier.id);
				}
				return false;
			});
		}
		return true;
	}

	async getRecentlyProducts (params?: any): Promise<void> {
		const requestParams: IRelatedProductParams = {
			slug: Boolean(typeof params === "string") ? params : params?.slug || "",
			extensions: this.extensionsIRelatedProduct || []
		};

		await this.actionGetRecentlyProducts(requestParams);
	}
	async getSameProducts (params: any): Promise<void> {
		const requestParams: IRelatedProductParams = {
			slug: Boolean(typeof params === "string") ? params : params?.slug || "",
			extensions: this.extensionsIRelatedProduct || []
		};

		await this.actionGetSameProducts(requestParams);
	}
	async getRelatedProducts (params?: any): Promise<void> {
		const requestParams: IRelatedProductParams = {
			slug: Boolean(typeof params === "string") ? params : params?.slug || "",
			extensions: this.extensionsIRelatedProduct || []
		};

		await this.actionGetRelatedProducts(requestParams);
	}

	/** Создать дефолтный набор модификаторов (пока не использую) */
	public createDefaultModifierSet(product: IProduct): IModifierSet {
		const modifications: SelectedModifications = {};

		const modifierSet: IModifierSet = {
			modifications: {},
			modifier: null,
			id: 1,
			quantity: 1,
			quantityLimit: product.quantity,
			price: product.price,
			oldPrice: product.oldPrice || null,
			productId: product.id,
		};

		if (product.hasModifications) {
			const firstModificaton = product?.modifications[0];
			if (firstModificaton) {

				const modificationVariantIDs = firstModificaton.variants.map((variant) => +variant.id);
				product.variants.forEach((group) => {
					const groupVariantsIDs = group.variants.map((variant) => +variant.id);
					modificationVariantIDs.forEach((modificationVariantId) => {
						if (groupVariantsIDs.includes(modificationVariantId)) {
							modifications[+group.groupId] = modificationVariantId;
						}
					})
				});

				const modifier = {
					id: firstModificaton.id,
					productId: product.id,
					name: firstModificaton.name,
					oldPrice: firstModificaton.oldPrice || null,
					price: firstModificaton.price,
					variants: firstModificaton.variants,
					quantity: firstModificaton.quantity,
				};

				modifierSet.modifications = modifications;
				modifierSet.modifier = modifier;
				modifierSet.quantityLimit = firstModificaton.quantity;
				modifierSet.oldPrice = firstModificaton.oldPrice || null;
				modifierSet.price = firstModificaton.price;
			}
		}
		return modifierSet;
	}

	/** Создать набора модификаторов */
	public createModifierSet(id: number, productId: number, quantity = 0, modifier?: IModifier): IModifierSet {
		return {
			id,
			quantity,
			productId,
			modifier: modifier || null,
			price: modifier?.price || null,
			oldPrice: modifier?.oldPrice || null,
			quantityLimit: modifier?.quantity || 0,
			modifications: {},
		};
	}

	/** Обновить набора модификаторов */
	public updateModifierSet(id: number, params: Partial<IModifierSet>): void {
		const modifierSetIndex = this.modifierSets.findIndex((modifierSet) => modifierSet.id === id);
		if (modifierSetIndex > -1) {
			this.modifierSets.splice(modifierSetIndex, 1, {
				...this.modifierSets[modifierSetIndex],
				...params,
			});
		}
	}

	/** Получить ID для набора модификаторов */
	public getNextModifierSetId(): number {
		const ids = this.modifierSets.map((modifierSet) => +(modifierSet.id || 1));

		if (ids.length) {
			return Math.max(...ids) + 1;
		}
		return 1;
	}

	/** Удалить набор модификаторов */
	public removeModifierSet(id: number): void {
		this.modifierSets = this.modifierSets.filter((modifierSet) => modifierSet.id !== id);
	}

	/** Установить кол-во для набора модификаторов */
	public setModifierSetQuantity(id: number, quantity: number): void {
		this.updateModifierSet(id, { quantity });
	}

	/** Установить вариант модификатора для набора */
	public setModifierSetVariant(id: number, groupId: number, variantId: number): void {
		const modifierSet = this.findModifierSet(id);

		if (modifierSet) {
			this.updateModifierSet(id, {
				modifications: {
					...modifierSet.modifications,
					[groupId]: variantId,
				}
			});
		}
	}

	/** Найти набор модификаторов по его ID */
	public findModifierSet(id: number): IModifierSet | undefined {
		return this.modifierSets.find((modifier) => modifier.id === id);
	}

	/** Найти все совместимые модификаторы для переданного ID варианта */
	public findAllСompatibleModifiers(selectedVariantsIDs: number[]): IModifier[] {
		const compatibleModifiers: IModifier[] = [];
		const { product } = this;

		if (product) {
			(product.modifications || []).forEach((modifcation) => {
				/**
				 * Нахожу все модификаторы в вариантах которого
				 * есть ID искомого варианта
				 * */
				const variantsIds = modifcation.variants.map((variant) => +variant.id);

				// Находим что бы все выбранные пункы были в модификации
				const active = selectedVariantsIDs.every(item => variantsIds.indexOf(item) !== -1);

				if (active) {
					compatibleModifiers.push({
						id: modifcation.id,
						productId: product.id,
						name: modifcation.name,
						oldPrice: modifcation.oldPrice || 0,
						price: modifcation.price,
						variants: modifcation.variants,
						quantity: modifcation.quantity,
					});
				}
			})
		}
		return compatibleModifiers;
	}

	/** Найти все совместимые варианты для переданного ID варианта */
	public findAllСompatibleVariants(selectedVariantsIDs: number[]): IModifierVariant[] {
		const compatibleModifiers = this.findAllСompatibleModifiers(selectedVariantsIDs);
		const compatibleVariants: IModifierVariant[] = [];

		/** Нахожу все ID вариантов у доступных модификаторов */
		compatibleModifiers.forEach((modifier) => {
			modifier.variants.forEach((variant) => compatibleVariants.push(variant as IModifierVariant));
		});

		/** Возвращаю все ID вариантов исключая переданный */
		// return compatibleVariants.filter((variant) => variant.id !== +variantId);
		return compatibleVariants;
	}

	/** Найти первый подходящий модификатор продкута по ID вариантов(полное совпадение) */
	public findModifierByVariantIDs(ids: number[]): IModifier | undefined {
		if (this.product && this.product.modifications) {
			return this.product.modifications.find(
				modification =>
					modification.variants
						.map((variant) => +variant.id)
						.every((id) => ids.includes(+id))
			) as IModifier | undefined;
		}

		return undefined;
	}

	/** Получить дефолтынй модификатор, такой ставлю всем, пока не уточнены параметры */
	public getDefaultModifier(): IModifier {
		return {
			id: null,
			price: this.product?.price || null,
			oldPrice: this.product?.oldPrice || null,
			productId: this.product?.id || null,
			name: this.product?.name || 'null',
			quantity: this.product?.quantity || 0,
		} as IModifier;
	}

	/** Добавить набор модификаторов */
	public addModifierSet(productId: number, id?: number): void {
		const modifier = this.getDefaultModifier();
		const modifierSetId = id || this.getNextModifierSetId();
		const newModifieSet = this.createModifierSet(modifierSetId, productId, 1, modifier);

		this.modifierSets.push(newModifieSet);
	}

	/** Установить выбранный вариант для набора модификаторов */
	public setVariant(setId: number | string, groupId: number | string, variantId: number | string): void {
		const modifierSetId = +setId;

		this.setModifierSetVariant(modifierSetId, +groupId, +variantId);
		const modifierSet = this.findModifierSet(modifierSetId);

		// Сброс всех значение после выбранного, для правильной работы выбора модификаций
		const newModifications = {...modifierSet?.modifications || {}};
		Object.keys(modifierSet?.modifications || {}).map((key) => {
			if (+key > +groupId) {
				delete newModifications[+key];
			}
		})
		this.updateModifierSet(modifierSetId, {
			modifications: newModifications
		});

		if (modifierSet) {
			const ids = Object.values(modifierSet.modifications || {}).map(Number);
			const modifier = this.findModifierByVariantIDs(ids) || this.getDefaultModifier();

			if (modifier) {
				/** Пересчитать допустимое количество */
				const currentQuantity = +(modifierSet.quantity as number);
				const quantityLimit = +(modifier.quantity as number);

				const quantity = currentQuantity > quantityLimit
					? currentQuantity - (currentQuantity - quantityLimit)
					: currentQuantity;

				this.updateModifierSet(modifierSetId, {
					modifier,
					quantity,
					quantityLimit,
				});
			}
		}
	}

	/** Найти группу вариантов по ID варианта */
	public findVariantGroup(variantId: number): IProductVariant | undefined {
		if (this.product) {
			return this.product.variants.find((group) =>
				group.variants.some((variant) => +variant.id === +variantId));
		}
		return undefined;
	}

	/** Тут формирую список доступных опций для выбора */
	public getVariantsForModifierSet(setId = 1): IModifierVariantOptionsGroup[] {
		const modifierSet = this.findModifierSet(setId);
		const variantOptionsGroups: IModifierVariantOptionsGroup[] = [];

		if (this.product) {
			const modifierSetModifications = (modifierSet?.modifications || {});
			/** ID выбраных групп модификатора */

			const selectedGroupIDs = Object.keys(modifierSetModifications).map(Number);
			/** ID выбраных вариантов модификатора */

			const selectedVariantsIDs = Object.values(modifierSetModifications).map(Number);
			/** Всего выбраных групп */

			const selectedGroupsCount = selectedGroupIDs.length;
			/** Совместимые варианты с выбранными */

			const compatibleVariantsIDs: number[] = [];
			/** Тут ищу все совместимые варианты с выбранными */


			this.findAllСompatibleVariants(selectedVariantsIDs).forEach((variant) => compatibleVariantsIDs.push(+variant.id));

			this.product.variants.forEach((group, groupOrder) => {
				const variantOptions: IModifierVariantOption[] = [];
				group.variants.forEach((variant) => {
					const variantId = +variant.id;
					const selected = selectedVariantsIDs.includes(variantId)
					/**
					 * Считать доступным, если уже выбранно или если относится к первой группе
					 * или если этот вариант совместим с выбранным для текущего набора
					 */
					let enabled = selected || groupOrder === 0 || compatibleVariantsIDs.includes(variantId);

					if ( groupOrder > 0 && selectedGroupsCount > 0 ) {
						const prevCompatibleVariantsIDs: number[] = [];
						const prevSelectedVariantsIDs = [...selectedVariantsIDs].splice(0, groupOrder);
						this.findAllСompatibleVariants(prevSelectedVariantsIDs).forEach((variant) => prevCompatibleVariantsIDs.push(+variant.id));

						enabled = selected || prevCompatibleVariantsIDs.includes(variantId);
					}

					variantOptions.push({
						selected,
						enabled,
						label: variant.label,
						image: variant.image,
						id: variantId,
					});
				});

				const selected = selectedGroupIDs.includes(+group.groupId)

				/**
				 * Считать доступным, если уже выбранно или если индекс равен
				 * количеству выбраных групп
				 */
				const enabled = groupOrder === selectedGroupsCount || selected;

				const variantOptionsGroup: IModifierVariantOptionsGroup = {
					enabled,
					selected,
					name: group.name,
					id: group.groupId,
					variants: variantOptions,
				}
				variantOptionsGroups.push(variantOptionsGroup);
			});
		}

		return variantOptionsGroups;
	}

	/** Вспомогательный метод получения количества и его лимита для модификации */
	public getModifierSetQuantity(setId: number): IModifierQuantity {
		const modifierSet = this.findModifierSet(setId);
		const modifierSetQuantity: IModifierQuantity = {
			current: 0,
			limit: 0,
		};
		if (modifierSet) {
			modifierSetQuantity.current = modifierSet.quantity || 0;
			modifierSetQuantity.limit = modifierSet.quantityLimit;
		}
		return modifierSetQuantity;
	}

	/** Увеличить количество в заданном наборе */
	public increaseQuantity(setId = 1): void {
		const { current, limit }  = this.getModifierSetQuantity(setId);
		if (current + 1 <= limit) {
			this.setModifierSetQuantity(setId, current + 1);
		}

	}

	/** Уменьшить количество в заданном наборе */
	public decreaseQuantity(setId = 1): void {
		const { current }  = this.getModifierSetQuantity(setId);
		if (current > 1) {
			this.setModifierSetQuantity(setId, current - 1);
		}
	}

	public getPageHead(): IPageHead {
		const head: IPageHead = {
			title: '',
			meta: [],
		};

		if (this.product) {
			const { name } = this.product;
			head.title = this.product.seo?.title || name;
			head.meta.push({name: 'og:title', hid: "og:title", property: "og:title", content: this.product.seo?.title || name},);

			head.meta.push({ hid: "metadescription", name: 'description', content: this.product.seo?.description });
			head.meta.push({ hid: "og:description", name: 'og:description', property: "og:description", content: this.product.seo?.description });
		}
		this.isUrlWithId()
		return head;
	}

	async loadProduct(): Promise<void> {
		const [slug] = this.$route.path.split('/').reverse();

		const requestParams: IGetProductParams = {
			slug,
			extensions: this.extensions || []
		};
		const product = await this.getProduct(requestParams);
		if (product) {
			this.product = JSON.parse(JSON.stringify(product));
			this.addModifierSet(product.id, 1);
		} else {
			this.$nuxt.error({ statusCode: 404, message: `Product ${slug} not found` });
		}
	}
	public isUrlWithId() {
		const path = this.$route.fullPath;
		const _slugFromUrl = path.split('/').reverse()?.[0];

		if (this.product) {
			if (_slugFromUrl === String(this.product.id) ) {
				return this.$nuxt.context.redirect(301, String(this.product.slug))
			}
		}
	}
}
